import React, { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/AddOutlined";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import {
  NAV_TREE,
  navTarget,
  nodeUrl,
  nodeVisible,
  scopeReady,
} from "../../shared/navigationRegistry";
import { resolveViewedYearId } from "../../shared/schoolYear";
import { NavDrawerContext } from "../shared/NavDrawerButton";
import {
  NAV_CARET_ICON_SIZE,
  NAV_CARET_SLOT_SX,
  NAV_ROW_ACTIVE,
  NAV_ROW_ACTIVE_HOVER,
  NAV_ROW_SX,
  NAV_SURFACE_HOVER,
} from "./navSurface";

// Launchpad icon ink: off-white at 54%, straight from the design. The lead icon
// and the expand chevron both use it, so they sit behind the white label rather
// than competing with it. The "+" stays pure white - it is the call to action.
export const NAV_ICON_COLOR = "#F5F5F58A";

// Renders the full app navigation tree (NAV_TREE) as an accordion inside the
// School left drawer, right after the school switcher. Top-level links with
// sub-links collapse/expand independently (multiple may be open at once);
// links whose scope is not satisfied render greyed-out and disabled, matching
// the DashboardMenu grid. Visibility and scope gating reuse the registry's
// nodeVisible / scopeReady helpers so this stays in lockstep with the modal.
// Trailing "+" that adds the row's destination to the dashboard bookmarks.
// Only rendered when an onAddBookmark callback is supplied (the Dashboard Menu
// passes one; the real sidebar/drawer does not, so it stays unchanged).
function AddBookmarkButton({
  node,
  onAddBookmark,
  bookmarkedIds = [],
  onGradient = false,
}) {
  if (!onAddBookmark) return null;
  const added = bookmarkedIds.includes(node.id);
  const size = onGradient ? 18 : 20;
  const addedColor = onGradient ? NAV_ICON_COLOR : "#00000080";
  const addColor = onGradient ? "#FFFFFF" : "primary.main";
  return (
    <IconButton
      size="small"
      disabled={added}
      aria-label={
        added
          ? `${node.label} already bookmarked`
          : `Add ${node.label} bookmark`
      }
      onClick={(e) => {
        e.stopPropagation();
        onAddBookmark(node.id);
      }}
      sx={{
        ml: 1,
        ...(onGradient && {
          p: 0,
          "&.Mui-disabled": { color: addedColor },
        }),
      }}
    >
      {added ? (
        <BookmarkIcon sx={{ fontSize: size, color: addedColor }} />
      ) : (
        <AddIcon sx={{ fontSize: size, color: addColor }} />
      )}
    </IconButton>
  );
}

// Fixed-width leading slot showing the expand/collapse state. It is a plain
// indicator now, not a button - the whole parent row is the expand trigger
// (groups carry no route, so clicking one only toggles its sub-links). For
// leaves (and child rows) it renders an empty box of the same width so every
// row's icon and label columns line up.
export function ExpandSlot({
  hasChildren,
  open,
  onGradient = false,
  onDark = false,
  rotate = false,
}) {
  const chevronColor = onGradient || onDark ? NAV_ICON_COLOR : "#00000080";
  // Nav groups get a single chevron that rotates a quarter turn when the group
  // opens, so the icon animates instead of swapping glyphs. Both menus that
  // render the tree (launchpad and sidebar) use it, so the same row reads the
  // same way in either place. Callers that are not a nav group - the school
  // switcher trigger - keep the up/down glyph pair.
  const rotating = onGradient || rotate;
  const indicator = rotating ? (
    <ChevronRightIcon
      sx={{
        fontSize: onGradient ? 18 : NAV_CARET_ICON_SIZE,
        color: chevronColor,
        transform: open ? "rotate(90deg)" : "none",
        transition: "transform 200ms",
      }}
    />
  ) : (
    <ExpandMoreIcon
      sx={{ fontSize: NAV_CARET_ICON_SIZE, color: chevronColor }}
    />
  );

  return (
    <Box
      sx={{
        ...NAV_CARET_SLOT_SX,
        ...(onGradient && { width: 23, mr: 0, justifyContent: "flex-start" }),
      }}
    >
      {hasChildren &&
        (open && !rotating ? (
          <ExpandLessIcon
            sx={{ fontSize: NAV_CARET_ICON_SIZE, color: chevronColor }}
          />
        ) : (
          indicator
        ))}
    </Box>
  );
}

// `onGradient` renders the launchpad treatment: bare rows in white type over
// the brand gradient, no containers of their own. `onDark` keeps the roomier
// list geometry but paints it for a dark surface (the nav sidebar/drawer): white
// labels, the launchpad's off-white icon alpha, white hover washes.
export default function SchoolNavMenu({
  appProps,
  schoolSlug,
  schoolYearId = null,
  navigationMode = "spa",
  onAddBookmark = null,
  bookmarkedIds = [],
  onGradient = false,
  onDark = false,
}) {
  const spaNavigate = useNavigate();
  const location = useLocation();
  const { close: closeDrawer } = useContext(NavDrawerContext);

  // Preselect a year the same way DashboardMenu does (URL param, then the
  // staff's last selection, then the active school year, then the first
  // available) so year-scoped links resolve to a sensible default instead of
  // staying greyed-out. The drawer has no year selector, so this is purely
  // derived - never user-picked here.
  const yearId = resolveViewedYearId(appProps, schoolYearId);

  const ctx = {
    schoolId: appProps?.school?.id ?? null,
    schoolSlug,
    school: appProps?.school ?? null,
    permissions: appProps?.permissions ?? {},
    yearId,
  };

  const nodes = useMemo(
    () => NAV_TREE.filter((node) => nodeVisible(node, ctx)),
    [ctx.schoolId, ctx.schoolSlug, ctx.yearId]
  );

  // Which link is active depends on the real browser path. In SPA mode
  // react-router's location is in sync with window.location; in "browser" mode
  // the navbar lives in a MemoryRouter (legacy v1 pages) whose location is
  // decoupled from the address bar, so read window.location directly. Match on
  // the path only - query params (school_year, term) are context, not identity.
  const currentPath =
    navigationMode === "browser" ? window.location.pathname : location.pathname;
  const isActive = (node) => nodeUrl(node, ctx)?.split("?")[0] === currentPath;

  // Default-open the section that holds the active link. Groups have no page of
  // their own, so a section opens only when one of its children is active.
  const initialExpanded = useMemo(() => {
    const state = {};
    nodes.forEach((node) => {
      if (!node.children) return;
      const childActive = node.children.some((child) => isActive(child));
      if (childActive) state[node.id] = true;
    });
    return state;
  }, [nodes, currentPath]);

  const [expanded, setExpanded] = useState(initialExpanded);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleNavigate = (node) => {
    const target = navTarget(node, ctx);
    if (!target || !scopeReady(target, ctx)) return;
    const url = target.resolve(ctx);
    if (navigationMode === "browser") {
      window.location.assign(url);
    } else if (!target.v1) {
      spaNavigate(url);
    } else {
      window.location.href = url;
    }
    closeDrawer?.();
  };

  // Row chrome for the launchpad: a bare line of white type, the hover state a
  // faint white wash rather than the light list's gray. The 40px row pitch is
  // split 36px row + 4px gap (rather than 24 + 16) so the hover wash wraps the
  // label instead of leaving a thin bar in a wide trough. Hoisted so the parent
  // rows and their children share one definition.
  // Both dark treatments (launchpad gradient, dark sidebar) wash their rows in
  // white; the light list keeps the blue selection.
  const lightOnDark = onGradient || onDark;

  const rowBg = (isActive_) => {
    if (!isActive_) return "transparent";
    return lightOnDark ? NAV_ROW_ACTIVE : "#E3F2FD";
  };

  const rowHoverBg = (isActive_) => {
    if (lightOnDark) {
      return isActive_ ? NAV_ROW_ACTIVE_HOVER : NAV_SURFACE_HOVER;
    }
    return isActive_ ? "#D6EAFB" : "rgba(0, 0, 0, 0.04)";
  };

  const rowSx = (isActive_) => ({
    borderRadius: 1,
    backgroundColor: rowBg(isActive_),
    "&:hover": { backgroundColor: rowHoverBg(isActive_) },
    ...(onGradient ? { py: 0.75, px: 0.5, minHeight: 36 } : NAV_ROW_SX),
  });

  const labelSx = (isActive_) => ({
    flex: 1,
    fontSize: onGradient ? 14 : 16,
    fontWeight: isActive_ || onGradient ? 500 : 400,
    ...(isActive_ && { fontWeight: 600 }),
    color: lightOnDark ? "#FFFFFF" : "#000000",
  });

  // Icon column: 24px glyph + the design's 10px gutter on the gradient, the
  // roomier 26px + 38px slot in the light list.
  const iconSlotSx = { minWidth: onGradient ? 34 : 38 };
  const iconSx = {
    fontSize: onGradient ? 24 : 26,
    color: lightOnDark ? NAV_ICON_COLOR : "#00000080",
  };

  return (
    <Box sx={{ pb: onGradient ? 0 : 3 }}>
      <List
        sx={{
          p: 0,
          ...(onGradient && {
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }),
        }}
      >
        {nodes.map((node) => {
          const Icon = node.icon;
          const children = (node.children ?? []).filter((child) =>
            nodeVisible(child, ctx)
          );
          const hasChildren = children.length > 0;
          const ready = scopeReady(node, ctx);
          const open = Boolean(expanded[node.id]);
          // Groups never highlight as the current page (they have no route);
          // only leaf rows do.
          const active = hasChildren ? false : isActive(node);

          return (
            <Box key={node.id}>
              <ListItemButton
                aria-current={active ? "page" : undefined}
                aria-expanded={hasChildren ? open : undefined}
                aria-disabled={!ready}
                onClick={() =>
                  hasChildren ? toggle(node.id) : handleNavigate(node)
                }
                sx={{
                  ...rowSx(active),
                  opacity: ready ? 1 : 0.4,
                  cursor: ready ? "pointer" : "not-allowed",
                }}
              >
                {/* The whole row toggles a group open/closed; this slot just
                    shows the chevron state. Leaves get an empty slot so columns
                    line up. */}
                <ExpandSlot
                  hasChildren={hasChildren}
                  open={open}
                  onGradient={onGradient}
                  onDark={onDark}
                  rotate
                />
                <ListItemIcon sx={iconSlotSx}>
                  {Icon && <Icon sx={iconSx} />}
                </ListItemIcon>
                <Typography sx={labelSx(active)}>{node.label}</Typography>
                <AddBookmarkButton
                  node={node}
                  onAddBookmark={onAddBookmark}
                  bookmarkedIds={bookmarkedIds}
                  onGradient={onGradient}
                />
              </ListItemButton>

              {hasChildren && (
                <Collapse in={open} timeout={200} unmountOnExit>
                  <List
                    sx={{
                      p: 0,
                      ...(onGradient && {
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pt: 0.5,
                      }),
                    }}
                  >
                    {children.map((child) => {
                      const childReady = scopeReady(child, ctx);
                      const childActive = isActive(child);
                      return (
                        <ListItemButton
                          key={child.id}
                          aria-current={childActive ? "page" : undefined}
                          aria-disabled={!childReady}
                          onClick={() => handleNavigate(child)}
                          sx={{
                            ...rowSx(childActive),
                            opacity: childReady ? 1 : 0.4,
                            cursor: childReady ? "pointer" : "not-allowed",
                          }}
                        >
                          {/* Empty expand + icon slots so child labels align
                              under the parent label. */}
                          <ExpandSlot
                            hasChildren={false}
                            onGradient={onGradient}
                            onDark={onDark}
                            rotate
                          />
                          <ListItemIcon sx={iconSlotSx} />
                          <Typography sx={labelSx(childActive)}>
                            {child.label}
                          </Typography>
                          <AddBookmarkButton
                            node={child}
                            onAddBookmark={onAddBookmark}
                            bookmarkedIds={bookmarkedIds}
                            onGradient={onGradient}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    </Box>
  );
}
